import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { TextField } from '@mui/material'
import React from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { LOGO_ACCEPT, useCompanyFormModel } from './company-form-model'

const CompanyForm = () => {
    const model = useCompanyFormModel()
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} title="Datos de la empresa">
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title="Datos de la empresa"
                    description="Esta información aparece en cotizaciones, correos y comunicados que envías a tus clientes."
                    backTo={model.backTo}
                    backLabel="Configuración"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Logo</h2>
                    <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                        <span className="flex h-28 w-44 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-k-line bg-k-sunken">
                            {model.logoSrc
                                ? <img src={model.logoSrc} alt={form.razonSocial || 'Logo de la empresa'} className="max-h-full max-w-full object-contain p-2" />
                                : <ImageOutlinedIcon className="text-k-muted-2" style={{ fontSize: 40 }} />}
                        </span>
                        <label className={`flex flex-1 cursor-pointer items-center gap-3.5 self-stretch rounded-2xl border-[1.5px] border-dashed border-k-line-strong bg-k-surface p-4 hover:bg-k-hover ${model.uploading ? 'pointer-events-none opacity-60' : ''}`}>
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] bg-tone-blue text-tone-blue-ink">
                                <UploadFileOutlinedIcon />
                            </span>
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-extrabold text-k-ink">
                                    {model.uploading ? 'Subiendo logo…' : model.logoSrc ? 'Cambiar logo' : 'Subir logo'}
                                </span>
                                <span className="text-xs text-k-muted">JPG o PNG, de preferencia con fondo transparente.</span>
                            </span>
                            <input
                                type="file"
                                accept={LOGO_ACCEPT}
                                className="sr-only"
                                disabled={model.uploading}
                                onChange={(event) => { model.uploadLogo(event.target.files?.[0]); event.target.value = '' }}
                            />
                        </label>
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5">
                        <TextField className="v2-field" variant="filled" fullWidth required label="Razón social" value={form.razonSocial} onChange={(event) => change.razonSocial(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth label="Dirección" value={form.direccion} onChange={(event) => change.direccion(event.target.value)} />
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Datos de contacto</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <div className="v2-phone">
                            <span className="v2-phone-label">Teléfono primario<span>*</span></span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.tel1}
                                onChange={(value) => change.tel1(value)}
                                inputProps={{ required: true, name: 'tel1' }}
                            />
                            <p className="v2-phone-hint">Con lada, por ejemplo +52 123 456 7890.</p>
                        </div>
                        <div className="v2-phone">
                            <span className="v2-phone-label">Teléfono secundario</span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.tel2}
                                onChange={(value) => change.tel2(value)}
                                inputProps={{ name: 'tel2' }}
                            />
                        </div>
                        <TextField className="v2-field" variant="filled" fullWidth required type="email" label="Correo primario" value={form.correo1} onChange={(event) => change.correo1(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth type="email" label="Correo secundario" value={form.correo2} onChange={(event) => change.correo2(event.target.value)} />
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Redes sociales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                        <TextField className="v2-field" variant="filled" fullWidth label="Facebook" placeholder="https://facebook.com/tu-agencia" value={form.facebook} onChange={(event) => change.facebook(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth label="YouTube" placeholder="https://youtube.com/@tu-agencia" value={form.youtube} onChange={(event) => change.youtube(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth label="TikTok" placeholder="https://tiktok.com/@tu-agencia" value={form.tiktok} onChange={(event) => change.tiktok(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth label="X (Twitter)" placeholder="https://x.com/tu-agencia" value={form.twitter} onChange={(event) => change.twitter(event.target.value)} />
                        <TextField className="v2-field md:col-span-2" variant="filled" fullWidth label="Sitio web" placeholder="https://tu-agencia.com" value={form.web} onChange={(event) => change.web(event.target.value)} />
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy || model.uploading}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar empresa
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default CompanyForm
